'use client';

import ToggleButton, { ToggleElementType } from '@pixum/toggle-button';
import Button, { ButtonVariants } from '@pixum/button';
import { Icon } from '@pixum/icon';
import { Dialog, DialogContent, DialogFooter, DIALOG_ROLES } from '@pixum/web-dialog';
import { useEffect, useRef } from 'react';
import { CartAddressFields } from '../../address-form/CartAddressFields';
import type { BillingAddressDialogProps } from '../../../types';
import { BILLING_FIELD_NAME_PREFIX, DESTINATION_TYPES } from '../../../constants';
import styles from '../../../CartAddressStep.module.css';
import { scrollToError } from '../../../utils/scrollToError';

/** Renders the editable billing address dialog and its destination-type toggle. */
export function BillingAddressDialog({
  billingAddress,
  billingType,
  billingToggleOptions,
  constants,
  locale,
  onBillingTypeChange,
  onClose,
  onFieldChange,
  onSave,
  isSaving = false,
  open,
  selectedBillingOption,
  fieldErrors = {}
}: BillingAddressDialogProps) {
  const dialogContentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const dialogContent = dialogContentRef.current;

    if (!open || Object.keys(fieldErrors).length === 0 || !dialogContent) {
      return;
    }

    scrollToError(dialogContent, fieldErrors, BILLING_FIELD_NAME_PREFIX);
  }, [fieldErrors, open]);

  return (
    <Dialog
      header={
        <div className={styles.cartAddressDialogHeader}>
          <Button
            label={constants['i18n_close']}
            variant="text"
            icon={<Icon name="close" ariaHidden={true} />}
            onClick={onClose}
          />
        </div>
      }
      content={
        <DialogContent>
          <div ref={dialogContentRef}>
            <fieldset className={styles.cartAddressSection}>
              <legend className={`${styles.cartAddressLegend} text--title3`}>
                {billingAddress ? constants['i18n_edit_billing_address'] : constants['i18n_add_billing_address']}
              </legend>
              <ToggleButton
                groupName="billing-type-mobile"
                className={styles.cartAddressToggle}
                toggleElementType={ToggleElementType.INPUT}
                options={billingToggleOptions}
                optionToBeSet={selectedBillingOption}
                onChange={onBillingTypeChange}
                label={constants['i18n_billing_address']}
              />
              <CartAddressFields
                address={billingAddress}
                locale={locale}
                company={billingType === DESTINATION_TYPES.COMPANY}
                isInDialog
                constants={constants}
                fieldNamePrefix={BILLING_FIELD_NAME_PREFIX}
                fieldErrors={fieldErrors}
                onFieldChange={onFieldChange}
                formScope="billing"
              />
            </fieldset>
          </div>
        </DialogContent>
      }
      footer={
        <DialogFooter
          className={styles.cartAddressDialogFooter}
          right={
            <Button type="button" isBusy={isSaving} onClick={onSave} variant={ButtonVariants.PRIMARY}>
              {constants['i18n_save']}
            </Button>
          }
          role={DIALOG_ROLES.DEFAULT}
        />
      }
      onClose={onClose}
      open={open}
      role={DIALOG_ROLES.DEFAULT}
      showCloseButton={false}
      fullScreen
    />
  );
}
